// @ts-strict-ignore
import {
	AudioChannel,
	AudioDeviceIconType,
	AudioDeviceInputChannelType,
	AudioDeviceOutputChannelType,
	AudioInputChannel,
	AudioOutputChannel,
	NetworkAudioInputChannel,
	PhoneAudioInputChannel,
} from "@models";
import {Component, Injector, OnInit} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {Translations} from "@translations";

export interface AUDIO_CHANNEL_TEMPLATE_INPUT extends MatDialogDataBaseInput {
	channelType: "input" | "output" | "phoneInput" | "networkInput";
	audioChannels: AudioChannel[];
}

export interface AUDIO_CHANNEL_TEMPLATE_OUTPUT extends MatDialogDataBaseOutput {
	audioChannels: (AudioOutputChannel & AudioInputChannel & PhoneAudioInputChannel & NetworkAudioInputChannel)[];
}

@Component({
	selector: "sim-audio-channel-template-dialog",
	templateUrl: "./audio-channel-template-dialog.component.html",
	styleUrls: ["./audio-channel-template-dialog.component.scss"],
	standalone: false,
})
export class AudioChannelTemplateDialogComponent
	extends BasicMaterialDialog<AudioChannelTemplateDialogComponent, AUDIO_CHANNEL_TEMPLATE_INPUT, AUDIO_CHANNEL_TEMPLATE_OUTPUT>
	implements OnInit
{
	T = Translations;
	public audioChannelTypes: {name: string; value: any}[];

	public amount: number = 1;
	public startChannelNumber: number = null;
	public numberingStyle: "every" | "even" | "odd" = "every";
	public selectedAudioChannelType: any;
	public nameTemplate: string = "Default [Index]";

	constructor(
		private translate: TranslateService,
		_injector: Injector,
	) {
		super(_injector);
	}

	ngOnInit() {
		super.ngOnInit();
		this.init();
	}

	protected init() {
		if (this.data.channelType === "input") {
			this.initializeAudioInputChannelType();
		} else if (this.data.channelType === "output") {
			this.initializeAudioOutputChannelType();
		} else if (this.data.channelType === "phoneInput") {
			this.initializePhoneAudioInputChannelType();
		} else if (this.data.channelType === "networkInput") {
			this.initializeNetworkAudioInputChannelType();
		}

		if (this.data.audioChannels) {
			this.startChannelNumber = this.data.audioChannels.length + 1;
		} else {
			this.startChannelNumber = 0;
		}
	}

	private getDefaultIconByAudioChannelType(audioChannelType: AudioDeviceInputChannelType): AudioDeviceIconType {
		switch (audioChannelType) {
			case AudioDeviceInputChannelType.Default:
				return AudioDeviceIconType.Microphone;
			case AudioDeviceInputChannelType.VOG:
				return AudioDeviceIconType.VOG;
			case AudioDeviceInputChannelType.VTP:
				return AudioDeviceIconType.VTP;
			case AudioDeviceInputChannelType.VttInputChannel:
				return AudioDeviceIconType.Trainer;
			case AudioDeviceInputChannelType.FlyingMic:
				return AudioDeviceIconType.WirelessMic;
			default:
				return null;
		}
	}

	ok() {
		const audioChannels: any[] = [];

		let multiplicationFactor = 1;

		if (this.numberingStyle === "even" || this.numberingStyle === "odd") {
			multiplicationFactor = 2;
		} else {
			multiplicationFactor = 1;
		}

		for (let i = 0; i < this.amount; i++) {
			const channelOffset = i * multiplicationFactor;

			const audioChannel = {
				channel: this.startChannelNumber + channelOffset,
				name: (this.nameTemplate || "Channel [ChannelNumber]")
					.replace("[ChannelNumber]", `${this.startChannelNumber + channelOffset}`)
					.replace("[Index]", `${i + 1}`),
				type: this.selectedAudioChannelType,
				icon: this.getDefaultIconByAudioChannelType(this.selectedAudioChannelType),
			};

			audioChannels.push(audioChannel);
		}

		this.close({action: "ok", audioChannels: <any>audioChannels});
	}

	checkInputAmount(): string {
		let errorMsg: string = "null";
		if (this.amount === 0 || this.amount === undefined) {
			errorMsg = this.translate.instant(this.T.CONFIGURATION_EDITOR_AUDIO_AUDIO_CHANNEL_TEMPLATE_NUMBER_OF_CHANNELS) + " is empty";
		} else if (this.amount < 1 || this.amount > 100) {
			errorMsg =
				this.translate.instant(this.T.CONFIGURATION_EDITOR_AUDIO_AUDIO_CHANNEL_TEMPLATE_NUMBER_OF_CHANNELS) +
				" must be in the range between 1 and 100";
		}

		return errorMsg;
	}

	private initializeAudioOutputChannelType() {
		this.audioChannelTypes = [
			{
				name: "Direct OutputChannel",
				value: AudioDeviceOutputChannelType.DefaultOutputChannel,
			},
			{
				name: "Vog Output Channel",
				value: AudioDeviceOutputChannelType.VogOutputChannel,
			},
			{
				name: "Vop Output Channel",
				value: AudioDeviceOutputChannelType.VopOutputChannel,
			},
			{
				name: "Training Room Output Channel",
				value: AudioDeviceOutputChannelType.TrainingRoomOutputChannel,
			},
			{
				name: "Vtt Output Channel",
				value: AudioDeviceOutputChannelType.VttOutputChannel,
			},
			{
				name: "ExStreamer",
				value: AudioDeviceOutputChannelType.Exstreamer,
			},
		];

		this.selectedAudioChannelType = this.audioChannelTypes[0].value;
	}

	private initializeAudioInputChannelType() {
		this.audioChannelTypes = [
			{
				name: "Default Input Channel",
				value: AudioDeviceInputChannelType.Default,
			},
			{name: "Vog Input Channel", value: AudioDeviceInputChannelType.VOG},
			{name: "Vop Input Channel", value: AudioDeviceInputChannelType.VTP},
			{
				name: "Vtt Input Channel",
				value: AudioDeviceInputChannelType.VttInputChannel,
			},
			{
				name: "Flying Mic Input Channel",
				value: AudioDeviceInputChannelType.FlyingMic,
			},
		];

		this.selectedAudioChannelType = this.audioChannelTypes[0].value;
	}

	private initializePhoneAudioInputChannelType() {
		this.audioChannelTypes = [
			{
				name: "Phone Input Channel",
				value: AudioDeviceInputChannelType.PhoneInputChannel,
			},
		];

		this.selectedAudioChannelType = this.audioChannelTypes[0].value;
	}

	private initializeNetworkAudioInputChannelType() {
		this.audioChannelTypes = [
			{name: "Vog Input Channel", value: AudioDeviceInputChannelType.VOG},
			{name: "Vop Input Channel", value: AudioDeviceInputChannelType.VTP},
		];

		this.selectedAudioChannelType = this.audioChannelTypes[0].value;
	}

	checkInputChannelNumber() {
		let errorMsg: string = "null";
		if (this.startChannelNumber === 0 || this.startChannelNumber === undefined) {
			errorMsg = this.translate.instant(this.T.CONFIGURATION_EDITOR_AUDIO_AUDIO_CHANNEL_TEMPLATE_START_CHANNEL_NUMBER) + " is empty";
		} else if (this.startChannelNumber < 1 || this.startChannelNumber > 1000) {
			errorMsg =
				this.translate.instant(this.T.CONFIGURATION_EDITOR_AUDIO_AUDIO_CHANNEL_TEMPLATE_START_CHANNEL_NUMBER) +
				" must be in the range between 1 and 100";
		}

		return errorMsg;
	}
}
